import { ZodError } from 'zod';
// Note: No override decorator is used here because we want to allow for overriding base class methods.

export class BaseError extends Error {
    status: number;

    constructor(
        message: string | undefined = undefined,
        status = 500,
        public opt?: { reason: string[] },
    ) {
        super(message || undefined);
        this.status = status;
        this.opt = opt;
    }

    toJSON() {
        return {
            message: this.message,
            name: this.constructor.name,
            opt: this.opt,
        };
    }
}

export class BadRequestError extends BaseError {
    constructor(
        public override message = 'Bad Request',
        public override opt?: { reason: string[] },
    ) {
        super(message);
        this.status = 400;
    }

    override toJSON() {
        return {
            message: this.message,
            name: this.constructor.name,
            opt: this.opt,
        };
    }
}

export class UnauthorizedError extends BaseError {
    constructor(
        public override message = 'Unauthorized',
        public override opt?: { reason: string[] },
    ) {
        super(message);
        this.status = 401;
    }

    override toJSON() {
        return {
            message: this.message,
            name: this.constructor.name,
            opt: this.opt,
        };
    }
}

export class ForbiddenError extends BaseError {
    constructor(
        public override message = 'Forbidden',
        public override opt?: { reason: string[] },
    ) {
        super(message);
        this.status = 403;
    }

    override toJSON() {
        return {
            message: this.message,
            name: this.constructor.name,
            opt: this.opt,
        };
    }
}

export class NotFoundError extends BaseError {
    constructor(
        public override message = 'Not Found',
        public override opt?: { reason: string[] },
    ) {
        super(message);
        this.status = 404;
    }

    override toJSON() {
        return {
            message: this.message,
            name: this.constructor.name,
            opt: this.opt,
        };
    }
}
export class GoneError extends BaseError {
    constructor(
        public override message = 'Gone',
        public override opt?: { reason: string[] },
    ) {
        super(message);
        this.status = 410;
    }

    override toJSON() {
        return {
            message: this.message,
            name: this.constructor.name,
            opt: this.opt,
        };
    }
}

export class TimeoutError extends BaseError {
    constructor(
        public override message = 'Timeout',
        public override opt?: { reason: string[] },
    ) {
        super(message);
        this.status = 408;
    }

    override toJSON() {
        return {
            message: this.message,
            name: this.constructor.name,
            opt: this.opt,
        };
    }
}

export class ConflictError extends BaseError {
    constructor(
        public override message = 'Conflict',
        public override opt?: { reason: string[] },
    ) {
        super(message);
        this.status = 409;
        this.opt = opt;
    }

    override toJSON() {
        return {
            message: this.message,
            name: this.constructor.name,
            opt: this.opt,
        };
    }
}

export class UnsupportedMediaTypeError extends BaseError {
    constructor(
        public override message = 'Unsupported MediaType',
        public override opt?: { reason: string[] },
    ) {
        super(message);
        this.status = 409;
    }

    override toJSON() {
        return {
            message: this.message,
            name: this.constructor.name,
            opt: this.opt,
        };
    }
}

export class UnprocessableEntityError extends BaseError {
    constructor(
        public override message = 'Unprocessable Entity',
        public override opt?: { reason: string[] },
    ) {
        super(message);
        this.status = 422;
        this.opt = opt;
    }

    override toJSON() {
        return {
            message: this.message,
            name: this.constructor.name,
            opt: this.opt,
        };
    }
}

export class GraphQLError extends BaseError {
    constructor(
        public override message = 'ValidationError',
        public override opt?: { reason: string[] },
    ) {
        super(message);
        this.status = 200;
        this.opt = opt;
    }

    override toJSON() {
        return {
            message: this.message,
            name: this.constructor.name,
            opt: this.opt,
        };
    }
}

export class DuplicateAggregateError extends ConflictError {
    constructor() {
        super('Aggregate already created');
    }
}

export class UnalteredError extends BadRequestError {
    constructor(message = 'Unaltered state') {
        super(message);
    }
}

export class CreditRatingError extends BaseError {
    constructor(public override opt: { error?: unknown; response?: unknown; reason: string[] }) {
        super(undefined);
        this.opt = this.prepareOpt(opt);
    }

    prepareOpt(opt?: { error?: unknown; response?: unknown; reason: string[] }) {
        const reason = opt?.reason || [];
        const error = opt?.error;
        let response;
        if (opt?.response) {
            response = {
                // @ts-expect-error data from axios response
                data: opt?.response?.data,
                // @ts-expect-error status from axios response
                status: opt?.response?.status,
                // @ts-expect-error statusText from axios response
                statusText: opt?.response?.statusText,
            };
            if (response.status !== 200) reason.push(`response status: ${response.status}`);
            if (response.statusText !== 'OK') reason.push(`response statusText: ${response.statusText}`);
        }
        if (error instanceof ZodError) reason.push('zod validation failed');
        else if (error instanceof Error) reason.push(error.message);
        else if (error) reason.push('failed with unknown error');
        return {
            error,
            reason,
            response,
        };
    }

    override get message() {
        return JSON.stringify(this.opt);
    }

    override toJSON() {
        return {
            message: this.message,
            name: this.constructor.name,
            opt: this.opt,
        };
    }
}
